import { Link, useNavigate } from 'react-router-dom';

function ScrollTopLink({ to, className, children }: { to: string; className: string; children: React.ReactNode }) {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}

export default function Footer() {
  return (
    <footer className="bg-[#0a1628] text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <i className="ri-music-2-fill text-3xl text-[#b8963e]"></i>
              <div>
                <h3 className="text-xl font-bold">Piano Movers UK</h3>
                <p className="text-xs text-gray-400">London Piano Specialists</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your trusted guide to professional piano removal services across London. Expert advice, price guides and recommended specialists covering all 32 boroughs.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-[#b8963e]">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <ScrollTopLink to="/" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm">
                  Home
                </ScrollTopLink>
              </li>
              <li>
                <ScrollTopLink to="/piano-removals-london" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm">
                  Piano Removals London
                </ScrollTopLink>
              </li>
              <li>
                <ScrollTopLink to="/grand-piano-removal-london" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm">
                  Grand Piano Removal
                </ScrollTopLink>
              </li>
              <li>
                <ScrollTopLink to="/upright-piano-removal-london" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm">
                  Upright Piano Removal
                </ScrollTopLink>
              </li>
              <li>
                <ScrollTopLink to="/piano-removal-cost-london" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm">
                  Piano Removal Costs
                </ScrollTopLink>
              </li>
              <li>
                <ScrollTopLink to="/piano-movers-london" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm">
                  Piano Movers London
                </ScrollTopLink>
              </li>
              <li>
                <ScrollTopLink to="/piano-storage-london" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm">
                  Piano Storage London
                </ScrollTopLink>
              </li>
              <li>
                <ScrollTopLink to="/contact-us" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm">
                  Contact Us
                </ScrollTopLink>
              </li>
            </ul>
          </div>

          {/* Company Information */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-[#b8963e]">Company Information</h4>
            <div className="space-y-2">
              <p className="text-white font-semibold text-sm">Piano Service North London</p>
              <p className="text-gray-400 text-sm">Trading as THE NORTH LONDON PIANO TRANSPORT 2 LTD</p>
              <p className="text-gray-400 text-sm">Company No. 06405076 | Registered in England &amp; Wales</p>
              <p className="text-gray-400 text-sm">Established 2007 | Fully Insured £5M Public Liability</p>
              <p className="text-gray-400 text-sm">176 Millicent Grove, London N13 6HS</p>
              <a href="tel:02034419463" className="block text-gray-400 hover:text-[#b8963e] transition-colors text-sm cursor-pointer">
                Tel: 020 3441 9463
              </a>
              <a href="mailto:thenorthpiano@googlemail.com" className="block text-gray-400 hover:text-[#b8963e] transition-colors text-sm cursor-pointer break-all">
                thenorthpiano@googlemail.com
              </a>
            </div>
          </div>

          {/* Our Websites */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-[#b8963e]">Our Websites</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.pianomovers.co.uk"
                  className="flex items-center gap-2 text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm"
                >
                  <i className="ri-global-line text-[#b8963e]"></i>
                  Piano Movers UK
                </a>
              </li>
              <li>
                <a
                  href="https://www.pianomoveteam.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer text-sm"
                >
                  <i className="ri-external-link-line text-[#b8963e]"></i>
                  Piano Service North London
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs text-center md:text-left">
              © 2026 The North London Piano Transport 2 Ltd | Company No. 06405076 | Registered in England &amp; Wales | All Rights Reserved | 176 Millicent Grove, London N13 6HS
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer">
                <i className="ri-twitter-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-[#b8963e] transition-colors cursor-pointer">
                <i className="ri-instagram-fill text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
